import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ArtSelect, { ArtSelectOption } from './ArtSelect';
import { ChipOption } from './ChipMultiSelect';
import { BomMaterial } from '../ArticleForm';

interface Props {
    options: ChipOption[]; // all available materials to pick from
    value: BomMaterial[]; // the materials currently chosen
    onChange: (next: BomMaterial[]) => void;
    hasError?: boolean;
}

const toArt = (list: { value?: any; label?: string }[]): ArtSelectOption[] =>
    list.map((o) => ({ value: o.value, label: String(o.label ?? '') }));

// Simple materials list for production tracking: pick WHICH materials go into the
// article. Quantity + unit are intentionally NOT collected here — these materials
// are only tracked, not stocked. (KuBIT's raw-material BOM, which does carry amounts
// and units, lives separately on the article's KuBIT components, not on this list.)
// Each row still keeps a default quantity:1 / unitId:null so the saved shape and any
// previously-stored amounts stay unchanged.
const MaterialBomEditor: React.FC<Props> = ({ options, value, onChange, hasError }) => {
    const intl = useIntl();

    const selectedIds = new Set(value.map((m) => String(m.value)));
    const available = options.filter((o) => !selectedIds.has(String(o.value)));

    const addMaterial = (v: number | string | null) => {
        if (v === null) return;
        const opt = options.find((o) => String(o.value) === String(v));
        if (!opt) return;
        onChange([...value, { value: opt.value, label: opt.label, quantity: 1, unitId: null }]);
    };

    const removeRow = (idx: number) => onChange(value.filter((_, i) => i !== idx));

    return (
        <div className={`art-bom art-bom--simple ${hasError ? 'has-error' : ''}`}>
            <div className="art-bom__add">
                <ArtSelect
                    value={null}
                    onChange={(v) => addMaterial(v)}
                    options={toArt(available)}
                    placeholder={intl.formatMessage({ id: 'admin.articles.form.materials.addPlaceholder', defaultMessage: 'Shto material...' })}
                    ariaLabel={intl.formatMessage({ id: 'admin.articles.form.materials.addPlaceholder', defaultMessage: 'Shto material...' })}
                />
            </div>

            {value.length === 0 ? (
                <div className="art-bom__empty">
                    <FormattedMessage id="admin.articles.form.materials.emptyHint" defaultMessage="Asnjë material i zgjedhur. Kliko më sipër për të shtuar." />
                </div>
            ) : (
                <div className="art-bom__rows">
                    <div className="art-bom__head">
                        <span><FormattedMessage id="admin.articles.form.bom.material" defaultMessage="Materiali" /></span>
                        <span aria-hidden="true" />
                    </div>
                    {value.map((m, idx) => (
                        <div className="art-bom__row" key={String(m.value)}>
                            <span className="art-bom__name">{m.label}</span>
                            <button
                                type="button"
                                className="art-bom__remove"
                                aria-label={intl.formatMessage({ id: 'common.remove', defaultMessage: 'Hiq' })}
                                onClick={() => removeRow(idx)}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MaterialBomEditor;
